import {
  CashbackCorrection,
  SavingDetails,
  SavingsTransactions,
} from "../../../types";
import { formatCurrency, TransactionType } from "../../../../../utils";
import { ProgressCounts, ProgressEntry } from "../../types";
import { dayOf } from "../../utils";
import { CORRECTION_FALLBACK_DETAIL, EXPLANATIONS } from "./constants";
import { grossTaxOf } from "./format";
import { ProgressGroup, ProgressRow, RowDescription } from "./types";

const positiveOrNull = (value: number | null) =>
  value !== null && value > 0 ? value : null;

const withNote = (transaction: SavingsTransactions, fallback: string): string =>
  transaction.note?.trim() || fallback;

const lockLabelOf = ({
  settlement,
  cashbackExpenseId,
}: SavingsTransactions): string | null => {
  if (settlement) {
    return settlement.kind === "CASHBACK"
      ? "Managed by a buy"
      : "Managed by a trade";
  }
  if (cashbackExpenseId != null) {
    return "Managed by an expense";
  }
  return null;
};

const expenseExplanationOf = (transaction: SavingsTransactions): string => {
  const earned = positiveOrNull(transaction.cashbackEarned);
  if (earned === null) {
    return EXPLANATIONS.expense;
  }
  if (!transaction.cashbackCredited) {
    return EXPLANATIONS.olderExpense;
  }
  return `The expense lowered the balance, and its ${formatCurrency(earned)} cashback raised it on the same date. Editing or deleting the expense moves or removes the cashback too.`;
};

const categoryPathOf = ({
  categoryName,
  subCategoryName,
}: SavingsTransactions): string =>
  [categoryName, subCategoryName].filter(Boolean).join(" › ") || "Expense";

const interestSubOf = (transaction: SavingsTransactions): string | null => {
  const grossTax = grossTaxOf(transaction);
  return grossTax && `Gross ${grossTax.gross} · tax ${grossTax.tax}`;
};

const describeDeposit = (transaction: SavingsTransactions): RowDescription => {
  const { settlement, fromAccountName } = transaction;
  const base = {
    type: "Deposit",
    sub: null,
    groups: ["deposit"] as ProgressGroup[],
  };
  if (settlement) {
    return {
      ...base,
      secondary: "Trade settlement",
      detail: withNote(
        transaction,
        `Sale on ${settlement.investmentAccountName}`,
      ),
      explanation: EXPLANATIONS.tradeSale,
    };
  }
  const fallback = fromAccountName ? `From ${fromAccountName}` : "Deposit";
  return {
    ...base,
    secondary: null,
    detail: withNote(transaction, fallback),
    explanation: EXPLANATIONS.deposit,
  };
};

const describeWithdrawal = (
  transaction: SavingsTransactions,
): RowDescription => {
  const { settlement, toAccountName } = transaction;
  const base = { type: "Withdrawal", sub: null };
  if (transaction.type === TransactionType.EXPENSE) {
    const hasCreditedCashback =
      transaction.cashbackCredited &&
      positiveOrNull(transaction.cashbackEarned) !== null;
    const groups: ProgressGroup[] = hasCreditedCashback
      ? ["withdrawal", "cashback"]
      : ["withdrawal"];
    return {
      ...base,
      secondary: "Expense",
      detail: withNote(transaction, categoryPathOf(transaction)),
      groups,
      explanation: expenseExplanationOf(transaction),
    };
  }
  if (settlement) {
    return {
      ...base,
      secondary: "Trade settlement",
      detail: withNote(
        transaction,
        `Buy on ${settlement.investmentAccountName}`,
      ),
      groups: ["withdrawal"],
      explanation: EXPLANATIONS.tradeBuy,
    };
  }
  const fallback = toAccountName ? `To ${toAccountName}` : "Withdrawal";
  return {
    ...base,
    secondary: null,
    detail: withNote(transaction, fallback),
    groups: ["withdrawal"],
    explanation: EXPLANATIONS.withdrawal,
  };
};

const describeCashback = (transaction: SavingsTransactions): RowDescription => {
  const isFromExpense = transaction.cashbackExpenseId != null;
  return {
    type: "Cashback",
    secondary: isFromExpense ? "From an expense" : null,
    detail: withNote(transaction, "Cashback received"),
    sub: null,
    groups: ["cashback"],
    explanation: isFromExpense
      ? EXPLANATIONS.expenseCashback
      : EXPLANATIONS.cashback,
  };
};

const describeMoving = (entry: ProgressEntry): RowDescription => {
  const { transaction } = entry;
  if (entry.kind === "deposit") {
    return describeDeposit(transaction);
  }
  if (entry.kind === "withdrawal") {
    return describeWithdrawal(transaction);
  }
  if (entry.kind === "cashback") {
    return describeCashback(transaction);
  }
  return {
    type: "Interest",
    secondary: null,
    detail: withNote(transaction, "Interest"),
    sub: interestSubOf(transaction),
    groups: ["interest"],
    explanation: EXPLANATIONS.interest,
  };
};

const movingRowOf = (entry: ProgressEntry): ProgressRow => {
  const { transaction } = entry;
  const lockLabel = lockLabelOf(transaction);
  return {
    key: `tx-${transaction.id}`,
    date: transaction.date,
    ...describeMoving(entry),
    amount: entry.amount,
    isNeutral: false,
    balanceAfter: entry.balanceAfter,
    cashbackEarned: positiveOrNull(transaction.cashbackEarned),
    transaction,
    correction: null,
    paidTo: null,
    lockLabel,
    isEditable: lockLabel === null,
  };
};

const sourcedRowOf = (transaction: SavingsTransactions): ProgressRow => {
  const { settlement } = transaction;
  const isBuy = settlement?.kind === "CASHBACK";
  const shared = {
    key: `tx-${transaction.id}`,
    date: transaction.date,
    sub: null,
    groups: ["cashback"] as ProgressGroup[],
    amount: Number(transaction.netAmount),
    isNeutral: true,
    balanceAfter: null,
    cashbackEarned: null,
    transaction,
    correction: null,
    isEditable: false,
  };
  if (isBuy) {
    return {
      ...shared,
      type: "Cashback buy",
      secondary: null,
      detail: withNote(transaction, settlement.investmentAccountName),
      paidTo: null,
      lockLabel: lockLabelOf(transaction),
      explanation: EXPLANATIONS.cashbackBuy,
    };
  }
  const { toAccountId, toAccountName } = transaction;
  const paidTo =
    toAccountId != null && toAccountName
      ? { accountId: toAccountId, name: toAccountName }
      : null;
  return {
    ...shared,
    type: "Cashback",
    secondary: "Paid elsewhere",
    detail: withNote(
      transaction,
      `Paid to ${toAccountName ?? "another account"}`,
    ),
    paidTo,
    lockLabel: null,
    explanation: EXPLANATIONS.payout,
  };
};

const correctionRowOf = (correction: CashbackCorrection): ProgressRow => ({
  key: `correction-${correction.id}`,
  date: correction.date,
  type: "Balance correction",
  secondary: null,
  detail: correction.note?.trim() || CORRECTION_FALLBACK_DETAIL,
  sub: null,
  groups: ["cashback"],
  amount: correction.amount,
  isNeutral: true,
  balanceAfter: null,
  cashbackEarned: null,
  transaction: null,
  correction,
  paidTo: null,
  lockLabel: null,
  isEditable: false,
  explanation: EXPLANATIONS.correction,
});

// Sorting by day alone is stable, so within a day moving rows stay first, then sourced rows, then corrections.
export const buildProgressRows = (
  saving: Pick<SavingDetails, "savingsTransactions" | "cashbackCorrections">,
  entries: ProgressEntry[],
): ProgressRow[] => {
  const sourced = (saving.savingsTransactions ?? [])
    .filter(({ movesBalance }) => !movesBalance)
    .reverse()
    .map(sourcedRowOf);
  const corrections = [...(saving.cashbackCorrections ?? [])]
    .reverse()
    .map(correctionRowOf);
  return [...entries.map(movingRowOf), ...sourced, ...corrections].sort(
    (a, b) => dayOf(b.date).localeCompare(dayOf(a.date)),
  );
};

export const progressCountsOf = (rows: ProgressRow[]): ProgressCounts => {
  const countOf = (group: ProgressGroup) =>
    rows.filter(({ groups }) => groups.includes(group)).length;
  return {
    deposits: countOf("deposit"),
    withdrawals: countOf("withdrawal"),
    interest: countOf("interest"),
  };
};
